import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  clearLoginBanner,
  getLoginMessageForReason,
  persistLoginBanner,
  readLoginBanner,
  resolveLoginBannerMessage,
} from '../../utils/loginMessages';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const loginReason = searchParams.get('reason');
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const [info, setInfo] = useState(() =>
    resolveLoginBannerMessage({
      stateMessage: location.state?.message,
      reason: loginReason,
    })
  );
  const [formData, setFormData] = useState({
    email: (location.state?.email || '').trim().toLowerCase(),
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const fromRedirect =
      location.state?.message || getLoginMessageForReason(loginReason);

    if (fromRedirect) {
      persistLoginBanner(fromRedirect);
      setInfo(fromRedirect);
    } else {
      const stored = readLoginBanner();
      if (stored) setInfo(stored);
    }

    if (location.state?.message) {
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: {} }
      );
    }
    // Run once on mount. sessionStorage keeps the banner across Strict Mode
    // remounts after router state is cleared.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDismissInfo = () => {
    clearLoginBanner();
    setInfo('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'email' ? value.toLowerCase() : value,
    });
    setError('');
    setErrorDetails('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Final guard: trim + lowercase the email even if autofill or paste
    // bypassed the onChange handler. Postgres treats `User@x.com` and
    // `user@x.com` as different rows, so we never want to send mixed case.
    const submission = {
      ...formData,
      email: (formData.email || '').trim().toLowerCase(),
    };
    try {
      const user = await login(submission);
      clearLoginBanner();
      setTimeout(() => {
        if (user?.role === 'admin' || user?.role === 'superadmin') {
          navigate('/admin', { replace: true });
        } else if (user?.role === 'vendor') {
          navigate('/vendor/dashboard', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }, 100);
    } catch (err) {
      const backendMsg = err.response?.data?.message;
      setError(backendMsg || 'Invalid credentials');
      setErrorDetails(
        backendMsg
          ? 'Please check your email and password and try again.'
          : 'Make sure your email and password are correct. If you forgot your password, use the link below to reset it.'
      );
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Login
          </Typography>
          
          {info && (
            <Alert severity="info" sx={{ mb: 2 }} onClose={handleDismissInfo}>
              {info}
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
              {errorDetails && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {errorDetails}
                </Typography>
              )}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              margin="normal"
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3, mb: 2 }}
            >
              Login
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                component="a"
                href="/forgot-password"
                variant="text"
                color="primary"
                sx={{ textTransform: 'none' }}
              >
                Forgot your password?
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
